
import { useState, useEffect } from 'react';
import { teamService } from '@/services/teamService';
import { Team, TeamPlayer } from '@/types/database';
import { toast } from 'sonner';

export const useTeams = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await teamService.getTeams();
      setTeams(data);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const createTeam = async (teamData: Omit<Team, 'team_id' | 'created_at' | 'updated_at'>) => {
    try {
      const newTeam = await teamService.createTeam(teamData);
      setTeams(prev => [...prev, newTeam]);
      toast.success('Team created successfully');
      return newTeam;
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
      throw error;
    }
  };

  const updateTeam = async (teamId: number, updates: Partial<Team>) => {
    try {
      const updatedTeam = await teamService.updateTeam(teamId, updates);
      setTeams(prev => prev.map(team => 
        team.team_id === teamId ? updatedTeam : team
      ));
      toast.success('Team updated successfully');
      return updatedTeam;
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('Failed to update team');
      throw error;
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  return {
    teams,
    loading,
    loadTeams,
    createTeam,
    updateTeam
  };
};

export const useTeamPlayers = (teamId: number | undefined) => {
  const [players, setPlayers] = useState<TeamPlayer[]>([]);
  const [loading, setLoading] = useState(false);

  const loadPlayers = async () => {
    if (!teamId || teamId === 0) {
      setPlayers([]);
      return;
    }
    
    try {
      setLoading(true);
      const data = await teamService.getTeamPlayers(teamId);
      setPlayers(data);
    } catch (error) {
      console.error('Error loading team players:', error);
      toast.error('Failed to load team players');
    } finally {
      setLoading(false);
    }
  };

  const addPlayer = async (userId: string, position?: string) => {
    try {
      const newPlayer = await teamService.addPlayerToTeam(teamId, userId, position);
      setPlayers(prev => [...prev, newPlayer]);
      toast.success('Player added to team');
      return newPlayer;
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player to team');
      throw error;
    }
  };

  const removePlayer = async (userId: string) => {
    try {
      await teamService.removePlayerFromTeam(teamId, userId);
      setPlayers(prev => prev.filter(p => p.user_id !== userId));
      toast.success('Player removed from team');
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player from team');
      throw error;
    }
  };

  useEffect(() => {
    loadPlayers();
  }, [teamId]);

  return {
    players,
    loading,
    loadPlayers,
    addPlayer,
    removePlayer
  };
};
